import React, { useContext } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { firebaseAuth } from '../../providers/AuthProvider';
import LoadingIndicator from '../common/LoadingIndicator';
import { STAFF_ROLE } from '../../Constants';

const AuthLoading = () => (
  <LoadingIndicator size="sm" className="py-10" />
);

export const RequireAuth = () => {
  const { user, loading } = useContext(firebaseAuth);
  const location = useLocation();
  if (loading) return <AuthLoading />;
  if (!user || !user.isAuthenticated) {
    return <Navigate to="/manage/sign-in" replace state={{ from: location }} />;
  }
  return <Outlet />;
};

export const RequireVerifiedEmail = () => {
  const { user, loading } = useContext(firebaseAuth);
  if (loading) return <AuthLoading />;
  if (!user?.isAuthenticated) return <Navigate to="/manage/sign-in" replace />;
  if (!user.emailVerified) return <Navigate to="/manage/verify-email" replace />;
  return <Outlet />;
};

export const RequireMFA = () => {
  const { user, loading } = useContext(firebaseAuth);
  if (loading) return <AuthLoading />;
  if (!user?.isAuthenticated) return <Navigate to="/manage/sign-in" replace />;
  if (!user.emailVerified) return <Navigate to="/manage/verify-email" replace />;
  if (!user.multiFactor) return <Navigate to="/manage/mfa" replace />;
  return <Outlet />;
};

export const RequireRole = ({ minRole }) => {
  const { user, loading } = useContext(firebaseAuth);
  if (loading) return <AuthLoading />;
  if (!user?.isAuthenticated) return <Navigate to="/manage/sign-in" replace />;
  if (typeof user.role !== 'number' || user.role > minRole) {
    return <Navigate to="/manage/permissions" replace />;
  }
  return <Outlet />;
};

// Deny direct URLs before a management screen can mount or fetch its data.
export const RequireStaffView = () => {
  const { user } = useContext(firebaseAuth);
  const { pathname } = useLocation();
  const registrationLink = pathname.match(/^\/(?:manage|onsite)\/registration\/([\w-]+)\/?$/);
  if (Number(user?.role) === STAFF_ROLE && registrationLink && registrationLink[1] !== 'new') {
    return <Navigate to={`/checkin/${registrationLink[1]}`} replace />;
  }
  const allowed = /^\/(manage|onsite)\/?$/.test(pathname) ||
    /^\/(manage|onsite)\/registrations(?:\/[\w-]+)?\/?$/.test(pathname) ||
    /^\/(manage|onsite)\/(profile|permissions)\/?$/.test(pathname);
  return Number(user?.role) === STAFF_ROLE && !allowed
    ? <Navigate to="/manage" replace /> : <Outlet />;
};

export const RequireStaffPublicView = () => {
  const { user, loading } = useContext(firebaseAuth);
  const { pathname } = useLocation();
  if (loading) return <AuthLoading />;
  if (Number(user?.role) === STAFF_ROLE && !/^\/checkin(?:\/[\w-]+)?\/?$/.test(pathname)) {
    return <Navigate to="/manage" replace />;
  }
  return <Outlet />;
};
